// src/components/tabs/DocumentPropertyComparison.tsx
import React, { useEffect, useState, useMemo } from 'react';
import { Collapse, Select } from 'antd';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { comparePropertyWithProperties } from '../../redux/api/documents/propertiesComparisonThunks';
import GroupedPropertiesList from '../property/GroupedPropertiesList';
import ComparedDocumentsList from '../ComparedDocumentsList';
import { Document, PropertyFull, ComparisonResult } from '../../types';
import { selectPropertyTypes } from '../../redux/api/documents/documentsSelectors';
import { selectDocumentsComputing } from '../../redux/api/documents/documentsSelectors';
import ComparePropertyAction from "../property/actions/ComparePropertyAction";
import OverlaySpinner from '../common/OverlaySpinner';

const { Option } = Select;

interface DocumentPropertyComparisonProps {
    documents: Record<string, Document>;
    documentData: Document;
}

const DocumentPropertyComparison: React.FC<DocumentPropertyComparisonProps> = ({ documents, documentData }) => {
    const dispatch = useAppDispatch();
    const propertyTypes = useAppSelector(selectPropertyTypes);
    const isComputing = useAppSelector(selectDocumentsComputing);

    const [leftPanelType, setLeftPanelType] = useState<string | undefined>(undefined);
    const [selectedProperty, setSelectedProperty] = useState<PropertyFull | undefined>(undefined);
    const [comparisonPropertyType, setComparisonPropertyType] = useState<string | undefined>(undefined);
    const [comparisonResults, setComparisonResults] = useState<Record<string, ComparisonResult[]>>({});

    const otherDocs = useMemo(() => {
        return Object.values(documents).filter((doc) => doc.id !== documentData.id);
    }, [documents, documentData.id]);

    useEffect(() => {
        let isMounted = true;

        const doCompare = async () => {
            if (!selectedProperty || !comparisonPropertyType) {
                return;
            }

            const promises = otherDocs.map(async (doc) => {
                const otherProps = doc.properties
                    ? doc.properties.filter((p) => p.propertyTypeId === comparisonPropertyType)
                    : [];
                if (otherProps.length === 0) {
                    return { docId: doc.id, results: [] as ComparisonResult[] };
                }

                const action = await dispatch(comparePropertyWithProperties({ propA: selectedProperty, otherProps }));
                if (comparePropertyWithProperties.fulfilled.match(action)) {
                    return { docId: doc.id, results: action.payload };
                } else {
                    console.error(`Comparison failed for document ID: ${doc.id}`);
                    return { docId: doc.id, results: [] as ComparisonResult[] };
                }
            });

            const resultsArray = await Promise.all(promises);
            const comparisonMap: Record<string, ComparisonResult[]> = {};
            resultsArray.forEach((item) => {
                comparisonMap[item.docId] = item.results;
            });
            if (isMounted) {
                setComparisonResults(comparisonMap);
            }
        };

        doCompare().catch((err) => console.error(err));

        return () => {
            isMounted = false;
        };
    }, [selectedProperty, comparisonPropertyType, otherDocs, dispatch]);

    const filteredProperties = (documentData.properties || []).filter((p) => {
        if (!leftPanelType) return true;
        return p.propertyTypeId === leftPanelType;
    });

    const handleSelectProperty = (property: PropertyFull) => {
        setSelectedProperty(property);
    };

    const handleLeftPanelTypeChange = (typeId: string) => {
        setLeftPanelType(typeId);
        setSelectedProperty(undefined);
    };

    const handleComparisonTypeChange = (typeId: string) => {
        setComparisonPropertyType(typeId);
    };

    return (
        <div className="flex">
            {/* Левая панель */}
            <div className="w-1/2 p-4">
                <div className="p-4 bg-white rounded-lg shadow h-[75vh] overflow-y-auto">
                    <h1 className="text-xl font-semibold mb-2">{documentData.title}</h1>
                    <Select
                        allowClear
                        placeholder="Фильтр по типу свойства (левая панель)"
                        style={{ width: '100%', marginBottom: 16 }}
                        value={leftPanelType}
                        onChange={handleLeftPanelTypeChange}
                    >
                        {propertyTypes.map((pt) => (
                            <Option key={pt.id} value={pt.id}>
                                {pt.name}
                            </Option>
                        ))}
                    </Select>
                    <Collapse bordered={false} className="rounded-lg">
                        <GroupedPropertiesList
                            properties={filteredProperties}
                            selectedProperty={selectedProperty}
                            actions={(property: PropertyFull, isSelected: boolean) => {
                                return [
                                    <ComparePropertyAction
                                        key="compare"
                                        property={property}
                                        isSelected={isSelected}
                                        onCompare={handleSelectProperty}
                                    />,
                                ];
                            }}
                            showNextButton={true}
                        />
                    </Collapse>
                </div>
            </div>

            {/* Правая панель */}
            <div className="w-1/2 p-4">
                {/* Оборачиваем контейнер в relative, чтобы спиннер позиционировался абсолютно */}
                <div className="p-4 bg-white rounded-lg shadow h-[75vh] overflow-y-auto relative">
                    <Select
                        placeholder="Выберите тип для сравнения"
                        style={{ width: '100%', marginBottom: 16 }}
                        value={comparisonPropertyType}
                        onChange={handleComparisonTypeChange}
                    >
                        {propertyTypes.map((pt) => (
                            <Option key={pt.id} value={pt.id}>
                                {pt.name}
                            </Option>
                        ))}
                    </Select>
                    <ComparedDocumentsList comparisonResults={comparisonResults} documents={documents} />
                    {/* Если идет вычисление, показываем оверлей-спиннер */}
                    {isComputing && <OverlaySpinner tip="Выполняется вычисление..." />}
                </div>
            </div>
        </div>
    );
};

export default DocumentPropertyComparison;
